/**
 * NotificationsPage — customer-facing inbox (iter56e). Triggered from
 * the bell icon in the marketplace storefront header (and any other
 * header that links to /notifications). Lists the last 50 alerts and
 * lets the user mark all as read.
 *
 * On mount, fires ``POST /api/notifications/read-all`` so unread
 * counts clear immediately and dispatches ``notifications:update``
 * so the bell badge in the header refreshes. Per-notification "tap to
 * navigate" is handled if the doc has ``payload.order_id`` (jump to
 * order detail).
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Loader2, ShoppingBag, Sparkles, IndianRupee } from "lucide-react";
import { api } from "../../lib/api";
import { useIsBlinkit } from "../../lib/storefrontTheme";
import { BkPageHeader } from "../../components/blinkit/BkUI";

const KIND_ICON = {
    ORDER: ShoppingBag,
    BOOKING: ShoppingBag,
    WALLET: IndianRupee,
    PROMO: Sparkles,
    GENERAL: Bell,
};

function timeAgo(iso) {
    if (!iso) return "";
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000) return "just now";
    const m = Math.floor(ms / 60_000);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
}

export default function NotificationsPage() {
    // Blinkit-theme tenants get the shared Blinkit header; the page body is the same for every theme.
    const blinkit = useIsBlinkit();
    const [rows, setRows] = useState(null);
    const [busy, setBusy] = useState(false);
    const nav = useNavigate();

    const load = async () => {
        try {
            const { data } = await api.get("/notifications");
            setRows(data || []);
        } catch {
            setRows([]);
        }
    };

    useEffect(() => {
        // First load + then mark everything as read after a small delay
        // so the user can actually SEE which rows were unread (blue dot)
        // before the auto-clear takes effect. The bell badge in the
        // header clears via the dispatched ``notifications:update`` event.
        let alive = true;
        let markTimer = null;
        (async () => {
            await load();
            if (!alive) return;
            markTimer = setTimeout(async () => {
                try { await api.post("/notifications/read-all"); } catch { /* ignore */ }
                if (!alive) return;
                window.dispatchEvent(new Event("notifications:update"));
                await load();
            }, 1500);
        })();
        return () => {
            alive = false;
            if (markTimer) clearTimeout(markTimer);
        };
    }, []);

    const handleClear = async () => {
        setBusy(true);
        try {
            await api.post("/notifications/read-all");
            window.dispatchEvent(new Event("notifications:update"));
            await load();
        } finally { setBusy(false); }
    };

    const handleTap = async (n) => {
        try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
        window.dispatchEvent(new Event("notifications:update"));
        const oid = n?.payload?.order_id;
        // Location Service Engine rows (e.g. a support reply) carry their own in-app link.
        const url = n?.payload?.url;
        if (typeof url === "string" && url.startsWith("/")) nav(url);
        else if (oid) nav(`/order/${oid}`);
    };

    return (
        <div className="pb-24" data-testid="notifications-page">
            {blinkit ? (
                <BkPageHeader title="Notifications" back="/profile" subtitle={rows && rows.length > 0 ? `${rows.length} alerts` : null} right={
                    rows && rows.some((n) => !n.is_read) ? (
                        <button onClick={handleClear} disabled={busy} data-testid="notif-mark-all-read"
                                className="h-9 px-2.5 rounded-lg text-[12.5px] font-bold text-primary inline-flex items-center gap-1.5 hover:bg-primary/5">
                            {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCheck size={14} />} Mark all read
                        </button>
                    ) : null
                } />
            ) : (
                <header className="px-4 pt-5 pb-3 bg-card border-b border-border flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-display font-bold inline-flex items-center gap-2">
                            <Bell size={22} className="text-primary" /> Notifications
                        </h1>
                        {rows && rows.length > 0 && (
                            <p className="text-xs text-muted-foreground mt-0.5">{rows.length} alerts</p>
                        )}
                    </div>
                    {rows && rows.some((n) => !n.is_read) && (
                        <button onClick={handleClear} disabled={busy}
                                data-testid="notif-mark-all-read"
                                className="text-xs font-bold text-primary inline-flex items-center gap-1.5">
                            {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCheck size={14} />}
                            Mark all read
                        </button>
                    )}
                </header>
            )}

            {rows === null ? (
                <div className="flex items-center justify-center py-12 text-muted-foreground">
                    <Loader2 size={16} className="animate-spin mr-2" /> Loading…
                </div>
            ) : rows.length === 0 ? (
                <div className="px-6 py-16 text-center" data-testid="notif-empty">
                    <Bell size={42} className="mx-auto text-muted-foreground/40 mb-3" />
                    <p className="text-base font-bold mb-1">You&apos;re all caught up</p>
                    <p className="text-sm text-muted-foreground mb-4">
                        New order updates, wallet activity and offers will land here.
                    </p>
                    <Link to="/" className="bg-primary text-primary-foreground rounded-xl px-4 py-2 text-sm font-semibold inline-block">Back to home</Link>
                </div>
            ) : (
                <ul className="lg:grid lg:grid-cols-2 lg:gap-x-6 lg:px-4" data-testid="notif-list">
                    {rows.map((n) => {
                        const Icon = KIND_ICON[n.kind] || Bell;
                        return (
                            <li key={n.id} className="border-b border-border last:border-b-0 lg:last:border-b">
                                <button type="button" onClick={() => handleTap(n)}
                                    data-testid={`notif-row-${n.id}`}
                                    className={`w-full px-4 py-3.5 flex items-start gap-3 text-left active:bg-muted/40 ${n.is_read ? "" : "bg-primary/5"}`}>
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${n.is_read ? "bg-muted text-muted-foreground" : "bg-primary/15 text-primary"}`}>
                                        <Icon size={18} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <p className={`text-sm flex-1 truncate ${n.is_read ? "font-semibold" : "font-bold"}`}>{n.title}</p>
                                            {!n.is_read && <span className="w-2 h-2 rounded-full bg-primary shrink-0" aria-label="Unread" />}
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>
                                        <p className="text-[10px] text-muted-foreground/70 mt-1">{timeAgo(n.created_at)}</p>
                                    </div>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
